import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
  DryRunShiftPlanResponse,
  OutputAdjustmentEntry,
} from '../../pages/checkout/model';
import { dryRunShiftPlan } from '../../services/checkoutService';
import { isFeatureEnabled } from '../../services/featureEnabledService';
import { getClockWindowNameBasedOnSelectedOption, generatePlan } from '../../services/shiftPlanService';
import { RootState } from '../types';

const CHECKOUT_FEATURE_NAME = 'RCC_OUTPUT_OVERRIDE';

// ─── State Interface ────────────────────────────────────────────────────────────

export interface CheckoutState {
  /** Whether the checkout feature is enabled (from isFeatureEnabled API) */
  isCheckoutEnabled: boolean | null;
  /** Full response from DryRunShiftPlan API (baseline, no adjustments) */
  dryRunResponse: DryRunShiftPlanResponse | null;
  /** Loading state for the DryRun API call */
  dryRunLoading: boolean;
  /** Error message from the DryRun API call */
  dryRunError: string | null;
  /** Response from DryRunShiftPlan API after applying adjustments (confirmation page) */
  appliedDryRunResponse: DryRunShiftPlanResponse | null;
  /** Loading state for the applied DryRun API call */
  appliedDryRunLoading: boolean;
  /** Error message from the applied DryRun API call */
  appliedDryRunError: string | null;
  /** User-built output adjustments (slider changes + disables) */
  outputAdjustments: OutputAdjustmentEntry[];
  /** Whether the confirmation page is showing */
  showConfirmation: boolean;
  /** Loading state for generate plan (verifyVolumes) */
  generatePlanLoading: boolean;
  /** Error from generate plan */
  generatePlanError: string | null;
}

// ─── Initial State ──────────────────────────────────────────────────────────────

const initialState: CheckoutState = {
  isCheckoutEnabled: null,
  dryRunResponse: null,
  
  dryRunLoading: false,
  dryRunError: null,
  appliedDryRunResponse: null,
  appliedDryRunLoading: false,
  appliedDryRunError: null,
  outputAdjustments: [],
  showConfirmation: false,
  generatePlanLoading: false,
  generatePlanError: null,
};

// ─── Async Thunks ───────────────────────────────────────────────────────────────

export const checkCheckoutFeatureEnabled = createAsyncThunk<
  boolean,
  void,
  { rejectValue: string }
>(
  'checkout/checkFeatureEnabled',
  async (_, { rejectWithValue }) => {
    try {
      const enabled = await isFeatureEnabled(CHECKOUT_FEATURE_NAME);
      return enabled;
    } catch (error: unknown) {
      return rejectWithValue('Failed to check feature flag');
    }
  }
);

export const fetchDryRunShiftPlan = createAsyncThunk<
  DryRunShiftPlanResponse,
  void,
  { rejectValue: string }
>(
  'checkout/fetchDryRunShiftPlan',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState() as RootState;
      const { stationCode, selectedDate, planId, selectedShift } = state.shiftPlan;

      if (!planId || !stationCode || !selectedDate || !selectedShift) {
        return rejectWithValue('Missing plan context');
      }

      const clockWindow = getClockWindowNameBasedOnSelectedOption(selectedShift);
      const { totalAttendanceOverride, rateOverrides, categoryOverrides } = state.rosterAndRates;
      const { processPathOverrides, trailerOverrides } = state.volumesAndWip;

      // Build functionReports from rateOverrides
      const functionReports = Object.entries(rateOverrides).map(([functionId, override]) => ({
        processPath: override.processPath || '',
        functionId: override.functionId || functionId,
        functionName: override.functionName || '',
        functionType: override.functionType || '',
        functionProductivity: {
          actualProductivity: override.override || 0,
          overrideReason: override.overrideReason,
          overrideComment: override.overrideComment,
        },
      }));

      // Build rosterData from categoryOverrides
      const rosterData = Object.entries(categoryOverrides).map(([categoryName, categoryOverride]) => ({
        propertyName: categoryName,
        overrideValue: categoryOverride.override || 0,
        overrideReason: categoryOverride.overrideReason,
        overrideComment: categoryOverride.overrideComment,
      }));

      // Combine all contexts into the baseline override JSON string
      const combinedInputOverrides = [
        {
          volumeContext: processPathOverrides.volumeContext,
          productivityRatesContext: { functionReports },
          totalAttendanceOverride,
          rosterData,
          trailerOverrides: trailerOverrides.length > 0 ? trailerOverrides : undefined,
        },
      ];

      const response = await dryRunShiftPlan({
        planId,
        warehouseId: stationCode,
        ofdDate: selectedDate,
        clockWindow,
        inputOverrides: JSON.stringify(combinedInputOverrides),
      });

      if (!response) {
        return rejectWithValue('Failed to fetch dry run shift plan');
      }

      return response;
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to fetch dry run shift plan';
      return rejectWithValue(message);
    }
  }
);

export const applyDryRunShiftPlan = createAsyncThunk<
  DryRunShiftPlanResponse,
  void,
  { rejectValue: string }
>(
  'checkout/applyDryRunShiftPlan',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState() as RootState;
      const { stationCode, selectedDate, planId, selectedShift } = state.shiftPlan;

      if (!planId || !stationCode || !selectedDate || !selectedShift) {
        return rejectWithValue('Missing plan context');
      }

      const clockWindow = getClockWindowNameBasedOnSelectedOption(selectedShift);
      const { totalAttendanceOverride, rateOverrides, categoryOverrides } = state.rosterAndRates;
      const { processPathOverrides, trailerOverrides } = state.volumesAndWip;
      const { outputAdjustments } = state.checkout;

      // Build functionReports from rateOverrides
      const functionReports = Object.entries(rateOverrides).map(([functionId, override]) => ({
        processPath: override.processPath || '',
        functionId: override.functionId || functionId,
        functionName: override.functionName || '',
        functionType: override.functionType || '',
        functionProductivity: {
          actualProductivity: override.override || 0,
          overrideReason: override.overrideReason,
          overrideComment: override.overrideComment,
        },
      }));

      // Build rosterData from categoryOverrides
      const rosterData = Object.entries(categoryOverrides).map(([categoryName, categoryOverride]) => ({
        propertyName: categoryName,
        overrideValue: categoryOverride.override || 0,
        overrideReason: categoryOverride.overrideReason,
        overrideComment: categoryOverride.overrideComment,
      }));

      // Combine all contexts with outputAdjustment included
      const combinedInputOverrides = [
        {
          volumeContext: processPathOverrides.volumeContext,
          productivityRatesContext: { functionReports },
          totalAttendanceOverride,
          rosterData,
          trailerOverrides: trailerOverrides.length > 0 ? trailerOverrides : undefined,
        },
      ];

      const response = await dryRunShiftPlan({
        planId,
        warehouseId: stationCode,
        ofdDate: selectedDate,
        clockWindow,
        inputOverrides: JSON.stringify(combinedInputOverrides),
        outputAdjustment: outputAdjustments.length > 0 ? outputAdjustments : null,
      });

      if (!response) {
        return rejectWithValue('Failed to apply changes');
      }

      return response;
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to apply changes';
      return rejectWithValue(message);
    }
  }
);

export const generatePlanThunk = createAsyncThunk<
  any,
  { planId: string; pageId: string },
  { rejectValue: string }
>(
  'checkout/generatePlan',
  async (params, { rejectWithValue }) => {
    try {
      const response = await generatePlan(params.planId, params.pageId);
      if (!response) {
        return rejectWithValue('Failed to generate plan');
      }
      return response;
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to generate plan';
      return rejectWithValue(message);
    }
  }
);

// ─── Slice ──────────────────────────────────────────────────────────────────────

const checkoutSlice = createSlice({
  name: 'checkout',
  initialState,
  reducers: {
    /**
     * Set or update an output adjustment for a process path.
     * If an entry with the same functionName already exists, it is replaced.
     */
    setOutputAdjustment(state, action: PayloadAction<OutputAdjustmentEntry>) {
      const index = state.outputAdjustments.findIndex(
        (entry) => entry.functionName === action.payload.functionName
      );
      if (index >= 0) {
        state.outputAdjustments[index] = action.payload;
      } else {
        state.outputAdjustments.push(action.payload);
      }
    },

    /**
     * Remove an output adjustment by functionName (reset a path to baseline).
     */
    removeOutputAdjustment(state, action: PayloadAction<string>) {
      state.outputAdjustments = state.outputAdjustments.filter(
        (entry) => entry.functionName !== action.payload
      );
    },

    /**
     * Clear all output adjustments (reset all paths to baseline).
     */
    clearOutputAdjustments(state) {
      state.outputAdjustments = [];
    },

    /**
     * Reset the entire checkout state (e.g., when navigating away).
     */
    resetCheckoutState() {
      return initialState;
    },

    /**
     * Dismiss the confirmation page and go back to the slider table.
     */
    dismissConfirmation(state) {
      state.showConfirmation = false;
      state.appliedDryRunResponse = null;
      state.appliedDryRunError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(checkCheckoutFeatureEnabled.fulfilled, (state, action) => {
        state.isCheckoutEnabled = action.payload;
      })
      .addCase(checkCheckoutFeatureEnabled.rejected, (state) => {
        state.isCheckoutEnabled = false;
      })
      .addCase(fetchDryRunShiftPlan.pending, (state) => {
        state.dryRunLoading = true;
        state.dryRunError = null;
      })
      .addCase(fetchDryRunShiftPlan.fulfilled, (state, action) => {
        state.dryRunLoading = false;
        state.dryRunResponse = action.payload;
      })
      .addCase(fetchDryRunShiftPlan.rejected, (state, action) => {
        state.dryRunLoading = false;
        state.dryRunError = action.payload as string;
      })
      .addCase(applyDryRunShiftPlan.pending, (state) => {
        state.appliedDryRunLoading = true;
        state.appliedDryRunError = null;
      })
      .addCase(applyDryRunShiftPlan.fulfilled, (state, action) => {
        state.appliedDryRunLoading = false;
        state.appliedDryRunResponse = action.payload;
        state.showConfirmation = true;
      })
      .addCase(applyDryRunShiftPlan.rejected, (state, action) => {
        state.appliedDryRunLoading = false;
        state.appliedDryRunError = action.payload as string;
      })
      .addCase(generatePlanThunk.pending, (state) => {
        state.generatePlanLoading = true;
        state.generatePlanError = null;
      })
      .addCase(generatePlanThunk.fulfilled, (state) => {
        state.generatePlanLoading = false;
      })
      .addCase(generatePlanThunk.rejected, (state, action) => {
        state.generatePlanLoading = false;
        state.generatePlanError = action.payload as string;
      });
  },
});

export const {
  setOutputAdjustment,
  removeOutputAdjustment,
  clearOutputAdjustments,
  resetCheckoutState,
  dismissConfirmation,
} = checkoutSlice.actions;

export default checkoutSlice.reducer;
